import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, TextField } from '@mui/material';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import { Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { useForm } from 'react-hook-form';

import Button from './Button';
import { checkPermission, getCurrentPosition, requestLocationPermission } from '../platform/geolocation';
import { V2ApiError } from '../services/client';
import { readLocationCache } from '../services/locationCache';
import { useV2GoogleMaps } from '../state/GoogleMapsProvider';

const DEFAULT_CENTER = { lat: 17.38714, lng: 78.491684 };

const searchInputStyle = {
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  width: '100%',
  maxWidth: '100%',
  height: 40,
  padding: '0 12px',
  borderRadius: 4,
  fontSize: 14,
  marginBottom: 12,
  outline: 'none',
};

function AddressFormFields({ register, errors }) {
  return (
    <>
      <TextField
        label="Full name"
        fullWidth
        {...register('userName', { required: 'Name is required' })}
        error={Boolean(errors.userName)}
        helperText={errors.userName?.message}
      />
      <TextField
        label="House / flat / door"
        fullWidth
        {...register('houseDetails', { required: 'Required' })}
        error={Boolean(errors.houseDetails)}
        helperText={errors.houseDetails?.message}
      />
      <TextField label="Street / area" fullWidth {...register('streetDetails')} />
      <TextField label="Landmark" fullWidth {...register('landmark')} />
      <TextField
        label="Pincode"
        fullWidth
        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
        {...register('pincode', {
          required: 'Pincode required',
          pattern: { value: /^\d{6}$/, message: '6-digit pincode' },
        })}
        error={Boolean(errors.pincode)}
        helperText={errors.pincode?.message}
      />
    </>
  );
}

/** Google map + Places — parent must only mount when REACT_APP_MAP_KEY is set. */
export function AddAddressFormMap({ onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { userName: '', houseDetails: '', streetDetails: '', landmark: '', pincode: '' } });

  const [latLong, setLatLong] = useState(DEFAULT_CENTER);
  const [searchResult, setSearchResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useV2GoogleMaps();

  const centerMap = useCallback((pos) => {
    if (!pos) return;
    mapRef.current?.setCenter(pos);
    mapRef.current?.setZoom(17);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const cached = await readLocationCache();
      const cachedLat = Number(cached?.lat);
      const cachedLng = Number(cached?.lng);
      if (!cancelled && Number.isFinite(cachedLat) && Number.isFinite(cachedLng)) {
        const cachedPos = { lat: cachedLat, lng: cachedLng };
        setLatLong(cachedPos);
        centerMap(cachedPos);
      }

      let perm = await checkPermission();
      if (perm === 'prompt') {
        perm = await requestLocationPermission();
      }
      if (perm !== 'granted' || cancelled) return;

      const pos = await getCurrentPosition();
      if (!cancelled && pos) {
        setLatLong(pos);
        centerMap(pos);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [centerMap]);

  const onMapLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance;
      mapInstance.setCenter(latLong);
      mapInstance.setZoom(17);
    },
    [latLong]
  );

  useEffect(() => {
    centerMap(latLong);
  }, [latLong, centerMap]);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required. Enable it in settings or pin the map manually.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location. Try again or move the pin on the map.');
        return;
      }
      setLatLong(pos);
      centerMap(pos);
    } finally {
      setLocating(false);
    }
  }, [centerMap]);

  const onMarkerDragEnd = useCallback((e) => {
    if (!e?.latLng) return;
    setLatLong({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const onAutoCompleteLoad = useCallback((ac) => {
    setSearchResult(ac);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (!searchResult) return;
    const place = searchResult.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) {
      setFormError('Pick a place from the suggestions list.');
      return;
    }
    setLatLong({ lat: loc.lat(), lng: loc.lng() });
    setFormError('');
  }, [searchResult]);

  const onSubmit = async (data) => {
    setFormError('');
    if (!isLoaded) {
      setFormError('Map is still loading. Please wait.');
      return;
    }
    if (!Number.isFinite(latLong.lat) || !Number.isFinite(latLong.lng)) {
      setFormError('Set a location on the map or search for a place.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong,
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}
    >
      {formError ? <Alert severity="error">{formError}</Alert> : null}
      {loadError ? (
        <Alert severity="error">Could not load Google Maps. Try again later.</Alert>
      ) : null}

      {!isLoaded ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
            disabled={!isLoaded || locating}
            onClick={useCurrentLocation}
            sx={{ borderRadius: '5px', alignSelf: 'stretch' }}
          >
            {locating ? 'Locating…' : 'Use current location'}
          </Button>
          <div style={{ width: '100%' }}>
            <Autocomplete onLoad={onAutoCompleteLoad} onPlaceChanged={onPlaceChanged}>
              <input type="text" placeholder="Search for your area" style={searchInputStyle} />
            </Autocomplete>
          </div>
          <Box sx={{ height: 220, width: '100%', maxWidth: '100%', borderRadius: 1, overflow: 'hidden' }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={latLong}
              zoom={17}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                rotateControl: false,
                minZoom: 8,
              }}
            >
              <Marker position={latLong} draggable onDragEnd={onMarkerDragEnd} />
            </GoogleMap>
          </Box>
        </>
      )}

      <AddressFormFields register={register} errors={errors} />

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button type="button" variant="outlined" fullWidth onClick={onCancel} sx={{ borderRadius: '5px' }}>
          Cancel
        </Button>
        <Button type="submit" fullWidth disabled={submitting || !isLoaded} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}

/** Fallback when REACT_APP_MAP_KEY is unset — manual coordinates. */
export function AddAddressFormManual({ onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userName: '',
      houseDetails: '',
      streetDetails: '',
      landmark: '',
      pincode: '',
      lat: String(DEFAULT_CENTER.lat),
      lng: String(DEFAULT_CENTER.lng),
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location.');
        return;
      }
      setValue('lat', String(pos.lat), { shouldValidate: true });
      setValue('lng', String(pos.lng), { shouldValidate: true });
    } finally {
      setLocating(false);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    setFormError('');
    const lat = Number(data.lat);
    const lng = Number(data.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setFormError('Enter valid latitude and longitude.');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setFormError('Coordinates out of range.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong: { lat, lng },
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {formError ? <Alert severity="error">{formError}</Alert> : null}
      <Alert severity="info">
        Set latitude and longitude for the delivery pin (e.g. from Google Maps). Add REACT_APP_MAP_KEY for map search.
      </Alert>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
        disabled={locating}
        onClick={useCurrentLocation}
        sx={{ borderRadius: '5px', alignSelf: 'flex-start' }}
      >
        {locating ? 'Locating…' : 'Use current location'}
      </Button>
      <TextField
        label="Latitude"
        fullWidth
        {...register('lat', { required: 'Required' })}
        error={Boolean(errors.lat)}
        helperText={errors.lat?.message}
      />
      <TextField
        label="Longitude"
        fullWidth
        {...register('lng', { required: 'Required' })}
        error={Boolean(errors.lng)}
        helperText={errors.lng?.message}
      />
      <AddressFormFields register={register} errors={errors} />
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button type="button" variant="outlined" fullWidth onClick={onCancel} sx={{ borderRadius: '5px' }}>
          Cancel
        </Button>
        <Button type="submit" fullWidth disabled={submitting} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}
